// Turning model numbers into the words and shapes the page shows.
// Kept out of the component so it can be tested without rendering React.

import type { Bucket, EvaluationDetail, ReportModel } from "@/lib/reportModel";

/** How a score reads to someone who does not think in percentages. */
export function bandFor(pct: number): { word: string; tone: Tone } {
  if (pct >= 85) return { word: "Excellent", tone: "good" };
  if (pct >= 60) return { word: "Solid", tone: "good" };
  if (pct >= 35) return { word: "Mixed", tone: "mid" };
  // Red is reserved for a genuine zero. Partial credit, however small, is still
  // credit and showing it in the same colour as "nothing at all" reads as a
  // harsher result than the marks support.
  if (pct > 0) return { word: "Weak", tone: "mid" };
  return { word: "Not shown", tone: "bad" };
}

export type Tone = "good" | "mid" | "bad" | "neutral";

export const TONE = {
  good: { text: "text-emerald-700", bar: "bg-emerald-500", soft: "bg-emerald-50", ring: "border-emerald-200" },
  mid: { text: "text-amber-700", bar: "bg-amber-500", soft: "bg-amber-50", ring: "border-amber-200" },
  bad: { text: "text-rose-700", bar: "bg-rose-500", soft: "bg-rose-50", ring: "border-rose-200" },
  neutral: { text: "text-slate-600", bar: "bg-slate-400", soft: "bg-slate-50", ring: "border-slate-200" },
} as const;

export function toneFor(pct: number): Tone {
  return bandFor(pct).tone;
}

/** Plain-English name for a question type. "system_design" helps nobody. */
export function questionTypeLabel(t: string | null | undefined): string {
  switch (String(t)) {
    case "mcq": return "Multiple choice";
    case "coding-mcq": return "Reading code";
    case "fill_blank": return "Fill in the blank";
    case "coding": return "Hands-on coding";
    case "essay": return "Written answer";
    case "system_design": return "System design";
    case "true_false": return "True or false";
    default: return "Question";
  }
}

/** What a skill measures, for a reader who does not know the term. */
export function skillBlurb(skill: string): string | null {
  const s = skill.toLowerCase();
  if (s.includes("system design")) return "Planning how the pieces of a large system fit together.";
  if (s.includes("engineering practice")) return "How they investigate and fix problems in live software.";
  if (s.includes("data engineering")) return "Moving and shaping data reliably at scale.";
  if (s.includes("problem solving")) return "Working through unfamiliar problems methodically.";
  if (s.includes("data structures")) return "Choosing the right way to organise data in code.";
  if (s.includes("testing")) return "Checking that code behaves as intended.";
  if (s.includes("backend")) return "The server side of an application.";
  if (s.includes("frontend")) return "What the user sees and interacts with.";
  if (s.includes("api")) return "How systems talk to each other.";
  if (s.includes("agile")) return "How they work within a delivery team.";
  return null;
}

/**
 * An icon family for skills with no brand of their own. "LLMs" and "MLOps" are
 * concepts, not products, and stamping the TensorFlow logo on them was simply
 * wrong: four identical orange marks down the page, none of them accurate.
 */
export type ConceptIcon =
  | "brain" | "layers" | "chat" | "wrench" | "database" | "server"
  | "blueprint" | "shield" | "users" | "target";

export function conceptIconFor(skill: string): ConceptIcon {
  const s = skill.toLowerCase();
  if (/\bllms?\b|language model|prompt|genai|generative/.test(s)) return "chat";
  if (/deep learning|neural/.test(s)) return "layers";
  if (/machine learning|\bml\b|\bai\b|data science/.test(s)) return "brain";
  if (/mlops|devops|ci\/cd|deployment|infrastructure/.test(s)) return "wrench";
  if (/system design|architecture/.test(s)) return "blueprint";
  if (/data|warehous|etl|elt|pipeline|analytics|big data/.test(s)) return "database";
  if (/api|backend|server|micro/.test(s)) return "server";
  if (/security|iam|auth/.test(s)) return "shield";
  if (/agile|communication|teamwork|collaborat/.test(s)) return "users";
  return "target";
}

export interface Finding {
  tone: Tone;
  title: string;
  body: string;
  points?: number;
  max?: number;
  caption?: string;
}

/**
 * The two or three things a hiring manager should take away, each traceable to
 * a scored answer. Written in they/them: pronouns are never inferred.
 */
export function deriveFindings(m: ReportModel, opts: { cvSkills?: string[] } = {}): Finding[] {
  const out: Finding[] = [];
  const closed = m.closedForm;
  const open = m.openEnded;
  const codeReview = m.rows.find((r) => r.code_review?.overall_score != null)?.code_review;

  if (closed && closed.max > 0) {
    const good = closed.pct >= 70;
    out.push({
      tone: toneFor(closed.pct),
      title: good ? "They write correct code" : "The hands-on work did not hold up",
      body: good
        ? `${closed.count} question${closed.count === 1 ? "" : "s"} with a checkable answer, ${Math.round(closed.pct)}% of the marks.` +
          (codeReview?.overall_score != null ? ` On the hands-on task the code was run against real test cases; our reviewer scored it ${codeReview.overall_score} out of 100.` : "")
        : `${closed.count} question${closed.count === 1 ? "" : "s"} with a checkable answer earned only ${Math.round(closed.pct)}% of the marks.`,
      points: closed.points, max: closed.max,
      caption: `${Math.round(closed.pct)}% of the marks`,
    });
  }

  if (open && open.max > 0) {
    const weak = open.pct <= 40;
    out.push({
      tone: toneFor(open.pct),
      title: weak ? "They did not explain their thinking" : "They can explain their reasoning",
      body: weak
        ? `${open.count} question${open.count === 1 ? "" : "s"} asked them to describe their approach in writing. That earned ${Math.round(open.pct)}% of the available marks.`
        : `${open.count} written question${open.count === 1 ? "" : "s"} earned ${Math.round(open.pct)}% of the marks, so they can put their reasoning into words.`,
      points: open.points, max: open.max,
      caption: `${Math.round(open.pct)}% of the marks`,
    });
  }

  // What the CV claims that this assessment never went near. Only worth saying
  // when we actually hold a claimed-skills list.
  const cv = (opts.cvSkills ?? []).filter(Boolean);
  if (cv.length > 1) {
    const tested = m.skills.map((s) => s.label.toLowerCase());
    const covered = cv.filter((c) =>
      tested.some((t) => t.includes(c.toLowerCase()) || c.toLowerCase().includes(t)));
    if (covered.length < cv.length) {
      out.push({
        tone: "mid",
        title: "Most of their CV was not tested",
        body: `Their profile lists ${cv.length} skills: ${cv.join(", ")}. This assessment covered ${covered.length === 0 ? "none of them" : covered.join(" and ")}. The rest is neither confirmed nor ruled out, so if it matters for the role it still needs checking.`,
        points: covered.length, max: cv.length,
        caption: "CV skills covered",
      });
    }
  }

  return out;
}

/** Points for the radar polygon, and the label anchors around it. */
export function radarGeometry(skills: Bucket[], r: number, cx: number, cy: number) {
  const n = skills.length;
  const at = (i: number, radius: number) => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
    return { x: cx + radius * Math.cos(a), y: cy + radius * Math.sin(a), a };
  };
  // Pre-computed strings, not a function: a function handed to <polygon points>
  // silently renders nothing and React only warns about it.
  const rings = [0.25, 0.5, 0.75, 1].map((f) =>
    skills.map((_, i) => { const p = at(i, r * f); return `${p.x.toFixed(1)},${p.y.toFixed(1)}`; }).join(" "));
  const shape = skills
    .map((s, i) => { const p = at(i, Math.max((r * s.pct) / 100, 1.5)); return `${p.x.toFixed(1)},${p.y.toFixed(1)}`; })
    .join(" ");
  const labels = skills.map((s, i) => {
    const p = at(i, r + 22);
    const anchor = Math.abs(Math.cos(p.a)) < 0.25 ? "middle" : Math.cos(p.a) > 0 ? "start" : "end";
    return { ...p, anchor, label: s.label, pct: Math.round(s.pct) };
  });
  const dots = skills.map((s, i) => at(i, Math.max((r * s.pct) / 100, 1.5)));
  return { rings, shape, labels, dots, at };
}

/** Proctoring events worth a client's attention, and what each one cost. */
const NOISE = new Set(["keystroke_analysis", "inactivity_analysis", "identity_verification"]);

export const EVENT_LABEL: Record<string, string> = {
  phone_detected: "Phone visible on camera",
  multiple_faces: "More than one person in view",
  no_face: "Nobody visible on camera",
  tab_switch: "Switched to another tab",
  visibility_change: "Changed browser tab",
  blur: "Switched away from the window",
  mouse_leave: "Mouse left the test window",
  right_click: "Right click",
  copy: "Copied text",
  paste: "Pasted text",
  idle: "Paused or idle",
  fullscreen_exit: "Left full screen",
  devtools: "Opened developer tools",
};

export function summariseEvents(events: any[]): { key: string; label: string; count: number; serious: boolean }[] {
  const counts = new Map<string, { count: number; serious: boolean }>();
  for (const e of events ?? []) {
    const key = String(e?.event_type ?? "");
    if (!key || NOISE.has(key)) continue;
    const serious = String(e?.severity ?? "").toLowerCase() === "warning"
      || String(e?.severity ?? "").toLowerCase() === "critical"
      || key === "phone_detected" || key === "multiple_faces" || key === "devtools";
    const cur = counts.get(key) ?? { count: 0, serious: false };
    counts.set(key, { count: cur.count + 1, serious: cur.serious || serious });
  }
  return [...counts.entries()]
    .map(([key, v]) => ({ key, label: EVENT_LABEL[key] ?? key.replace(/_/g, " "), ...v }))
    .sort((a, b) => Number(b.serious) - Number(a.serious) || b.count - a.count);
}

export function countNoise(events: any[]): number {
  return (events ?? []).filter((e) => NOISE.has(String(e?.event_type ?? ""))).length;
}

/** Split a coding submission into what we supplied and what they wrote. */
/**
 * Split a submission into the candidate's own lines and any harness we appended.
 *
 * Whether a starter template was actually put in front of the candidate is
 * decided per submission, from the submission itself, not from the template
 * stored on the question. Boilerplate only went live in June 2026: before that
 * a template was recorded against the question but never reached the editor,
 * so 35 of those 37 submissions share not one line with it. Claiming "we gave
 * them a template" there would take credit for work the candidate did from an
 * empty file.
 */
export function splitSubmission(code: string, starter: string | null | undefined) {
  const lines = (code ?? "").split("\n");
  const boundary = lines.findIndex((l) => /DO NOT MODIFY BELOW THIS LINE/i.test(l));
  const starterLines = new Set((starter ?? "").split("\n").map((l) => l.trim()).filter(Boolean));
  const own = lines.slice(0, boundary === -1 ? lines.length : boundary);
  const harness = boundary === -1 ? [] : lines.slice(boundary);

  // Evidence the template was really pre-filled: at least two of its lines
  // survive in what came back, and at least half of it. One shared line is not
  // evidence, since an obvious signature is what both sides would write anyway.
  const present = [...starterLines].filter((sl) => own.some((l) => l.trim() === sl)).length;
  const fromTemplate = starterLines.size > 0 && present >= 2 && present * 2 >= starterLines.size;

  const authored = own.map((l, i) => ({
    n: i + 1,
    text: l,
    // Without a template every non-empty line is theirs; with one, a line the
    // template did not contain is theirs.
    mine: l.trim().length > 0 && (!fromTemplate || !starterLines.has(l.trim())),
  }));
  return {
    authored,
    harness: harness.map((l, i) => ({ n: own.length + i + 1, text: l })),
    harnessUntouched: boundary !== -1,
    fromTemplate,
  };
}

/** True when a session's own `answers` entry holds something the candidate gave. */
export function hasRecordedAnswer(v: unknown): boolean {
  if (v == null) return false;
  if (typeof v === "string") return v.trim() !== "";
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    return ["answer", "answer_text", "answer_value", "selected_option", "code", "code_submission", "value"]
      .some((k) => hasRecordedAnswer(o[k]));
  }
  return true;
}

/**
 * Answered or not, for a question whose answer text we cannot show.
 *
 * An absent candidate_answers row is not proof of an absent attempt: before
 * July 2026 answers lived only in the session's `answers` JSON, which holds 516
 * zero-scored answers that were given and marked wrong. Any evidence of an
 * answer — marks, a review, the flag, or that JSON — counts as answered.
 */
export function missingAnswerStatus(
  row: { answered?: boolean | null; code_review?: unknown; points_awarded?: unknown; points_earned?: unknown },
  inSession = false,
): { headline: string; detail: string; attempted: boolean } {
  const scored = Number(row.points_awarded ?? row.points_earned ?? 0) > 0;
  if (row.code_review || scored || row.answered === true || inSession) {
    return {
      attempted: true,
      headline: "Answered",
      detail: "The candidate submitted an answer to this question. Its marks are shown above.",
    };
  }
  return {
    attempted: false,
    headline: "Not answered",
    detail: "The candidate did not submit an answer to this question, so it scores zero.",
  };
}

export function firstNameOf(full: string | null | undefined): string {
  const n = String(full ?? "").trim();
  if (!n) return "This candidate";
  return n.split(/\s+/)[0];
}

export function maskSurname(full: string | null | undefined, isPublic: boolean): string {
  const n = String(full ?? "").trim();
  if (!n) return "Candidate";
  if (!isPublic) return n;
  const parts = n.split(/\s+/);
  return parts.length === 1 ? parts[0] : `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
}
